import React, { useState, useEffect, useRef } from "react";
import { string, arrayOf, shape, bool, func } from "prop-types";
import cx from "classnames";

import useClickAway from "../../common/useClickAway.js";
import { initScrolledIntoView } from "../../common/scrollIntoView.js";
import MapHeading from "./MapHeading/MapHeading.jsx";
import ImageElement from "../../components/ImageElement/ImageElement.jsx";
import Icon from "../../components/Icon/Icon.jsx";
import PinDetails, { propTypes as PinProps } from "./PinDetails/PinDetails.jsx";
import TransitionWrapper from "./TransitionWrapper.jsx";

export const propTypes = {
  title: string,
  description: string,
  background: shape({
    src: string,
    alt: string,
  }),
  pageEditor: bool,
  dictionary: shape({
    worldMapLabel: string,
    exploreLabel: string,
  }),
  childMapPinItems: arrayOf(
    shape({
      ...PinProps,
      id: string,
      isActive: bool,
      xCoord: string,
      yCoord: string,
      hoverTitle: string,
      pinTitle: string,
    }),
  ),
  hasParent: bool,
  handleCloseChildMap: func,
  backToParentLinkText: string,
  shouldAutoFocus: bool,
};

const MultiMapChildApp = (props) => {
  const {
    title,
    description,
    background,
    pageEditor,
    dictionary,
    childMapPinItems,
    hasParent,
    handleCloseChildMap,
    backToParentLinkText,
    shouldAutoFocus,
  } = props;

  const breakpoint = 780;
  const [wideView, setWideView] = useState(window.innerWidth > breakpoint);

  const initialCoords = { x: 16, y: 40 };
  const firstPinOpen = childMapPinItems.findIndex((pin) => pin.isActive);

  const [activeItemIndex, setActiveItemIndex] = useState(
    wideView ? (firstPinOpen !== -1 ? firstPinOpen : 0) : -1,
  );
  const [activeTooltipIndex, setActiveTooltipIndex] = useState();
  const [focusOnOpen, setFocusOnOpen] = useState(!hasParent);

  const showingPinDetail = activeItemIndex !== -1;
  const containerRef = useRef();
  const mapRef = useRef();
  const openingBtnRef = useRef(null);
  const pinBtnRefs = useRef([]);
  const listBtnRefs = useRef([]);
  const activePinRef = useRef(null);
  const activeListRef = useRef(null);
  const backButtonRef = useRef(null);

  useEffect(() => {
    if (shouldAutoFocus) {
      backButtonRef.current.focus();
    }
  }, [shouldAutoFocus]);

  const activeItemRef = useClickAway({
    isOpen: activeItemIndex !== -1,
    setOpen: (value) => {
      setActiveItemIndex(-1);
    },
    buttonRef: openingBtnRef,
    ignoredRefs: [backButtonRef, activePinRef, activeListRef],
  });

  const activeTooltipRef = useClickAway({
    isOpen: activeTooltipIndex !== -1,
    setOpen: (value) => {
      setActiveTooltipIndex(-1);
    },
  });

  useEffect(() => {
    if (activeItemIndex === -1) {
      activePinRef.current = null;
      activeListRef.current = null;
      return;
    }

    activePinRef.current = pinBtnRefs.current[activeItemIndex] ?? null;
    activeListRef.current = listBtnRefs.current[activeItemIndex] ?? null;
  }, [activeItemIndex]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > breakpoint && !wideView) {
        setWideView(true);
      }

      if (window.innerWidth <= breakpoint && wideView) {
        setWideView(false);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [wideView]);

  useEffect(() => {
    initScrolledIntoView(mapRef.current);
  }, [mapRef]);

  const toggleActiveItem = (index, openerEl) => {
    if (pageEditor) {
      return;
    }

    if (index === activeItemIndex) {
      setActiveItemIndex(-1);
    } else {
      openingBtnRef.current = openerEl;
      setActiveItemIndex(index);
    }
  };

  const toggleActiveTooltip = (index) => {
    if (pageEditor) {
      return;
    }

    setFocusOnOpen(true);

    if (index === activeTooltipIndex) {
      setActiveTooltipIndex(-1);
    } else {
      setActiveTooltipIndex(index);
    }
  };

  const handleCloseDetailPin = () => {
    setActiveItemIndex(-1);

    if (openingBtnRef.current) {
      // requestAnimationFrame ensures previously hidden list buttons are re-rendered before focus
      requestAnimationFrame(() => {
        openingBtnRef.current.focus();
      });
    }
  };

  const returnLabel = () => {
    if (hasParent && wideView) {
      return dictionary?.worldMapLabel || "View world map";
    } else if (!wideView && showingPinDetail) {
      return backToParentLinkText || "Back to region";
    } else {
      return dictionary?.returnToMapLabel || "View world map";
    }
  };

  const closeHandler =
    showingPinDetail && !wideView ? handleCloseDetailPin : handleCloseChildMap;

  return (
    <section ref={containerRef} className="multi-map__child-map-wrap">
      {/* PAGE EDITOR PIN POSITION ON PARENT MAP */}
      {pageEditor && hasParent && (
        <div className="multi-map__ee-pin-coords">
          <div>Pin Coordinates on Parent Map</div>
          <div>X: XX%</div>
          <div>Y: XX%</div>
        </div>
      )}

      <div className="multi-map__container multi-map__child-map container">
        <MapHeading
          title={title}
          description={description}
          returnToParentLabel={returnLabel()}
          handleCloseChildMap={closeHandler}
          wideView={wideView}
          backButtonRef={backButtonRef}
        />

        {/* PAGE EDITOR VIEW COORDINATES INPUT */}
        {pageEditor && (
          <div className="multi-map__coordinates js-multi-map-coords">
            <label>
              <span>x:</span>
              <input
                className="js-multi-map-child-xcoord"
                type="number"
                defaultValue={initialCoords.x}
                name="x"
              />
            </label>
            <button
              className="js-coord-clipboard-x multi-map__copy-btn"
              aria-label="Copy x coordinate to clipboard"
            >
              <Icon iconName="clipboard" />
            </button>
            <label>
              <span>y:</span>
              <input
                className="js-multi-map-child-ycoord"
                type="number"
                defaultValue={initialCoords.y}
                name="y"
              />
            </label>
            <button
              className="js-coord-clipboard-y multi-map__copy-btn"
              aria-label="Copy x coordinate to clipboard"
            >
              <Icon iconName="clipboard" />
            </button>
          </div>
        )}

        <div
          ref={mapRef}
          className="js-multi-map-child-map js-multi-map-child-map multi-map__map-container"
        >
          <ImageElement
            className="multi-map__map"
            sizes="(min-width: 780px) 750px, 100vw"
            {...background}
          />

          {/* PAGE EDITOR DRAGGABLE PIN */}
          {pageEditor && (
            <button
              className="multi-map__pin js-multi-map-child-pin"
              aria-label={"move to pick a spot"}
              draggable={true}
              onClick={() => {}}
              style={{
                top: `${initialCoords.y}%`,
                left: `${initialCoords.x}%`,
                position: "absolute",
              }}
            >
              <span>1</span>
            </button>
          )}

          {!pageEditor && (
            <nav aria-label="our locations" className="multi-map__map-points">
              <ul className="multi-map__items">
                {childMapPinItems &&
                  childMapPinItems?.map((location, i) => (
                    <li key={i} className="multi-map__item">
                      <div
                        className="multi-map__overlay"
                        style={{
                          top: location.yCoord,
                          left: location.xCoord,
                          position: "absolute",
                        }}
                      >
                        <button
                          ref={(el) => (pinBtnRefs.current[i] = el)}
                          className={cx("multi-map__pin", {
                            active: activeItemIndex === i,
                          })}
                          aria-labelledby={`child-pin-${i}`}
                          aria-haspopup="dialog"
                          aria-expanded={activeItemIndex === i}
                          aria-controls="child-map-popup"
                          onClick={(e) => toggleActiveItem(i, e.currentTarget)}
                          onMouseEnter={() => toggleActiveTooltip(i)}
                          onFocus={() => toggleActiveTooltip(i)}
                          onBlur={() => toggleActiveTooltip(-1)}
                        >
                          <Icon
                            iconName="add"
                            className="multi-map__pin--icon"
                          />
                        </button>
                        <div
                          id={`child-pin-${i}`}
                          className={cx("multi-map__tooltip", {
                            active: activeTooltipIndex === i,
                          })}
                          role="tooltip"
                          aria-hidden
                          ref={
                            activeTooltipIndex === i ? activeTooltipRef : null
                          }
                        >
                          {location?.pinTitle}
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </nav>
          )}
        </div>

        {/* DETAIL PINS */}
        {!pageEditor &&
          childMapPinItems.map((location, index) => (
            <TransitionWrapper
              key={index}
              show={activeItemIndex === index && activeItemIndex !== -1}
              baseClass="pin-details__wrap"
              exitClass="pin-details__wrap--exiting"
              duration={250} // match CSS keyframe duration
            >
              <div
                id="child-map-popup"
                className="multi-map__popup"
                role="dialog"
                ref={activeItemIndex === index ? activeItemRef : null}
              >
                <PinDetails
                  {...childMapPinItems[index]}
                  handleClose={handleCloseDetailPin}
                  focusOnOpen={focusOnOpen}
                />
              </div>
            </TransitionWrapper>
          ))}
      </div>

      {/* DETAIL PINS EXPERIENCE EDITOR VIEW  */}
      {pageEditor && (
        <div className="multi-map__ee-detail-pins">
          {childMapPinItems.map((location, i) => (
            <div className="multi-map__popup" key={i}>
              <div className="multi-map__ee-pin-coords">
                <div>X: XX%</div>
                <div>Y: XX%</div>
              </div>
              <PinDetails
                {...location}
                handleClose={() => setActiveItemIndex(-1)}
                focusOnOpen={focusOnOpen}
              />
            </div>
          ))}
        </div>
      )}

      {/* MOBILE PIN LIST - NEEDS TO BE OUTSIDE OF MAP CONTAINER SO PINS STAY IN POSITION */}
      {!wideView && (
        <ul
          className={cx("multi-map__mobile-pin-list", {
            "multi-map__mobile-pin-list--hide": showingPinDetail,
          })}
        >
          {childMapPinItems?.map((location, i) => (
            <li className="multi-map__mobile-pin-item" key={i}>
              <button
                ref={(el) => (listBtnRefs.current[i] = el)}
                className="multi-map__pin-mobile"
                onClick={(e) => {
                  toggleActiveItem(i, e.currentTarget);
                }}
                aria-haspopup="dialog"
                aria-expanded={activeItemIndex === i}
                aria-controls="multi-map-child-dialog"
              >
                {location?.hoverTitle || location.title}
                <div className="multi-map__pin-mobile-cta">
                  <span>{dictionary?.exploreLabel}</span>
                  <Icon
                    iconName="collapse-content"
                    className="multi-map__pin-mobile-icon"
                  />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

MultiMapChildApp.propTypes = propTypes;
export default MultiMapChildApp;
